import { Injectable, Signal } from '@angular/core';
import { Experiences } from '@local/client-contracts';
import { filter, map, takeUntil } from 'rxjs';
import { observable, ValueStorage } from '@local/common';
import { isExternalAssistant, isGeneralAssistant, isChatAssistant, isAgenticAssistant } from '@local/common-web';
import { ChatErrorState, AssistantChatData } from '../views/chat-page/model';
import { SessionStorageService } from '@shared/services/session-storage.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { INIT_POSITION_POPUP, PopupRef, PopupService } from '@local/ui-infra';
import { AppPopupComponent, AppPopupData } from '../components/app-popup/app-popup.component';
import { ShowToasterService } from './show-toaster.service';
import { ExperiencesInvokerService } from './experiences-invoker.service';

@Injectable({
  providedIn: 'root',
})
export class ChatAssistantsService {
  private readonly PREVIEW_STORAGE_KEY = 'hiddenAssistants';
  private readonly rightButtonStylePin = { type: 'primary', size: 130, fontSize: 14 };
  private readonly leftButtonStylePin = { size: 130, fontSize: 14 };
  private readonly rightButtonStyleUnpin = { type: 'primary', size: 96, fontSize: 14 };
  private readonly leftButtonStyleUnpin = { size: 96, fontSize: 14 };
  private readonly PIN_POPUP_PROPERTIES = {
    message: 'This will effect all users it is shared with.',
    messageStyle: { fontSize: '14px', lineHeight: '20px', color: 'var(--color-text-secondary)' },
    showButtons: true,
    rightButtonStyle: null,
    leftButtonStyle: null,
    content: {
      secondaryButton: 'Cancel',
      primaryButton: '',
      title: '',
    },
    titleStyle: {
      fontSize: '16px',
      fontWeight: 700,
      lineHeight: '22px',
    },
  };
  private readonly POPUP_OPTIONS = { position: [{ ...INIT_POSITION_POPUP[0], offsetY: -40 }], width: '308px', closeOnClickOut: false };
  private hiddenAssistantsStorage: ValueStorage<string[]>;
  private compPopupRef: PopupRef<AppPopupComponent, AppPopupData>;

  hiddenAssistantsStorageValue: Signal<string[]>;

  constructor(
    private experiencesInvokerService: ExperiencesInvokerService,
    private sessionStorageService: SessionStorageService,
    private popupService: PopupService,
    private showToasterService: ShowToasterService
  ) {
    this.hiddenAssistantsStorage = this.sessionStorageService.getStore('roaming', 'account').entry<string[]>(this.PREVIEW_STORAGE_KEY);
    this.hiddenAssistantsStorageValue = toSignal(this.hiddenAssistantsStorage.current$);
  }

  @observable
  get assistantForChat$() {
    return this.experiencesInvokerService.all$.pipe(
      filter((all) => !!all),
      map((all) => all.filter((a) => a && this.isValidAssistantForChat(a)))
    );
  }

  @observable
  get assistantForSearch$() {
    return this.experiencesInvokerService.all$.pipe(
      filter((all) => !!all),
      map((all) => all.filter((a) => a && this.isValidAssistantForQuickSearch(a)))
    );
  }

  isValidAssistantForChat(assistant: Experiences.ExperienceItem, getDetails = false): ChatErrorState | boolean {
    if (!isChatAssistant(assistant)) {
      return getDetails ? 'not-relevant-type' : false;
    }
    if (isGeneralAssistant(assistant) && !assistant?.impersonateUser && !isExternalAssistant(assistant)) {
      return getDetails ? 'not-relevant-type' : false;
    }
    return true;
  }

  private isValidAssistantForQuickSearch(assistant: Experiences.ExperienceItem): boolean {
    if (!isChatAssistant(assistant)) {
      return false;
    }
    return isGeneralAssistant(assistant)
      ? assistant?.settings?.general?.displayOnQuickSearch
      : isAgenticAssistant(assistant)
        ? assistant?.settings?.displayOnQuickSearch
        : false;
  }

  addAssistantToHidden(assistant: AssistantChatData): void {
    const assistantId = assistant.id || assistant.unleashAiId;
    const currentHidden = this.hiddenAssistantsStorageValue() || [];
    if (!currentHidden.includes(assistantId)) {
      this.hiddenAssistantsStorage.set([...currentHidden, assistantId]);
    }
  }

  removeAssistantFromHidden(assistant: AssistantChatData): void {
    const assistantId = assistant.id || assistant.unleashAiId;
    const currentHidden = this.hiddenAssistantsStorageValue() || [];
    if (currentHidden.includes(assistantId)) {
      currentHidden.splice(currentHidden.indexOf(assistantId), 1);
      this.hiddenAssistantsStorage.set(currentHidden);
    }
  }

  togglePinned(assistant: AssistantChatData, value: boolean) {
    const properties = this.PIN_POPUP_PROPERTIES;
    if (value) {
      properties.content.primaryButton = 'Pin to sidebar';
      properties.content.title = `Are you sure you want to pin "${assistant.name}" to the sidebar?`;
      properties.rightButtonStyle = this.rightButtonStylePin;
      properties.leftButtonStyle = this.leftButtonStylePin;
    } else {
      properties.content.primaryButton = 'Unpin';
      properties.content.title = `Are you sure you want to Unpin "${assistant.name}" from the sidebar?`;
      properties.rightButtonStyle = this.rightButtonStyleUnpin;
      properties.leftButtonStyle = this.leftButtonStyleUnpin;
    }

    this.compPopupRef = this.popupService.open<AppPopupComponent, AppPopupData>(
      'center',
      AppPopupComponent,
      properties,
      this.POPUP_OPTIONS
    );

    const settings = isGeneralAssistant(assistant)
      ? {
          general: { pinToChatPage: value },
        }
      : { pinToChatPage: value };
    this.compPopupRef.compInstance.primaryButton.pipe(takeUntil(this.compPopupRef.destroy$)).subscribe(async () => {
      try {
        await this.experiencesInvokerService.updatePartialSettingsById(assistant.id || assistant.unleashAiId, settings);
        this.showToasterService.showToaster({
          title: value ? `Assistant "${assistant.name}" pinned successfully` : `Assistant "${assistant.name}" unpinned successfully`,
          id: 'assistant-pinned',
        });
      } catch {
        this.showToasterService.showToaster({
          title: value ? `Failed to pinned Assistant "${assistant.name}"` : `Failed to unpinned Assistant "${assistant.name}"`,
          id: 'assistant-pinned',
        });
      }
    });
    this.compPopupRef.compInstance.secondaryButton.pipe(takeUntil(this.compPopupRef.destroy$)).subscribe(() => {
      this.compPopupRef.close();
    });
  }
}
